/**
 * Scenarios – ImpactKpis, MonthlyResultsTable, ReconciliationPanel
 */

import { AlertCircle, Check, Scale } from "lucide-react";
import { formatMoney, formatVariance } from "@/components/tekledger-ui";
import type { Scenario } from "@workspace/api-client-react";
import { periodLabel } from "./utils";
import { ReconciliationStatus } from "./shared";

// ─── Impact KPIs ──────────────────────────────────────────────────────────────

export function ImpactKpis({ scenario }: { scenario: Scenario }) {
  const hasDrivers = (scenario.drivers?.length ?? 0) > 0;
  const impactComputed =
    hasDrivers &&
    (scenario.fyImpact !== 0 ||
      scenario.rolling12Impact !== 0 ||
      (scenario.monthlyResults?.length ?? 0) > 0);

  // When impact is not yet available, explain what is missing rather than
  // showing an unexplained value.
  const missingSub = hasDrivers
    ? "Recompute to project impact"
    : "Add a driver to model impact";

  const items = [
    {
      label: "Baseline Total",
      value: formatMoney(scenario.baselineTotal),
      sub: `FY${scenario.fiscalYear} through P${String(scenario.throughPeriod).padStart(2, "0")}`,
      tone: "",
      pending: false,
      testId: "kpi-baseline-total",
    },
    {
      label: "Outlook Total",
      value: formatMoney(scenario.outlookTotal),
      sub: impactComputed ? "With all drivers applied" : "Baseline (no impact applied)",
      tone: !impactComputed
        ? ""
        : scenario.outlookTotal > scenario.baselineTotal
        ? "bad"
        : scenario.outlookTotal < scenario.baselineTotal
        ? "good"
        : "",
      pending: false,
      testId: "kpi-outlook-total",
    },
    {
      label: "FY Impact",
      value: formatVariance(scenario.fyImpact),
      sub: impactComputed ? "Full year driver delta" : missingSub,
      tone:
        scenario.fyImpact > 0 ? "bad" : scenario.fyImpact < 0 ? "good" : "",
      pending: !impactComputed,
      testId: "kpi-fy-impact",
    },
    {
      label: "Rolling 12M Impact",
      value: formatVariance(scenario.rolling12Impact),
      sub: impactComputed ? "Next 12 months horizon" : missingSub,
      tone:
        scenario.rolling12Impact > 0
          ? "bad"
          : scenario.rolling12Impact < 0
          ? "good"
          : "",
      pending: !impactComputed,
      testId: "kpi-rolling-impact",
    },
  ];

  return (
    <div className="sc-kpi-row" data-testid="scenario-impact-kpis">
      {items.map((item) => (
        <div className="sc-kpi" key={item.label} data-testid={item.testId}>
          <span className="sc-kpi__label">{item.label}</span>
          {item.pending ? (
            <strong className="sc-kpi__pending">Not available yet</strong>
          ) : (
            <strong
              className={
                item.tone === "bad"
                  ? "bad-text"
                  : item.tone === "good"
                  ? "good-text"
                  : ""
              }
            >
              {item.value}
            </strong>
          )}
          <span className="sc-kpi__sub">{item.sub}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Monthly results table ─────────────────────────────────────────────────────

export function MonthlyResultsTable({ scenario }: { scenario: Scenario }) {
  if (!scenario.monthlyResults || scenario.monthlyResults.length === 0) {
    return (
      <div className="sc-empty-state" data-testid="monthly-results-empty">
        <Scale size={20} />
        <p>
          No monthly results yet. Add drivers and recompute to see projections.
        </p>
      </div>
    );
  }

  return (
    <div className="table-wrap" data-testid="monthly-results-table">
      <table>
        <thead>
          <tr>
            <th>Period</th>
            <th style={{ textAlign: "right" }}>Baseline</th>
            <th style={{ textAlign: "right" }}>Driver Impact</th>
            <th style={{ textAlign: "right" }}>Outlook</th>
          </tr>
        </thead>
        <tbody>
          {scenario.monthlyResults.map((row) => (
            <tr
              key={`${row.fiscalYear}-${row.period}`}
              data-testid={`monthly-row-${row.fiscalYear}-${row.period}`}
            >
              <td>
                {periodLabel(row.period)} {row.fiscalYear}
              </td>
              <td style={{ textAlign: "right" }}>
                {formatMoney(row.baselineAmount)}
              </td>
              <td
                style={{ textAlign: "right" }}
                className={
                  row.driverImpact > 0
                    ? "bad-text"
                    : row.driverImpact < 0
                    ? "good-text"
                    : ""
                }
              >
                {row.driverImpact === 0 ? "—" : formatVariance(row.driverImpact)}
              </td>
              <td style={{ textAlign: "right" }}>
                {formatMoney(row.outlookAmount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Reconciliation panel ──────────────────────────────────────────────────────

export function ReconciliationPanel({ scenario }: { scenario: Scenario }) {
  return (
    <div className="sc-reconciliation" data-testid="reconciliation-panel">
      <div className="sc-recon-row">
        <span className="sc-label">Baseline Total</span>
        <strong>{formatMoney(scenario.baselineTotal)}</strong>
      </div>
      <div className="sc-recon-row">
        <span className="sc-label">Outlook Total</span>
        <strong>{formatMoney(scenario.outlookTotal)}</strong>
      </div>
      <div className="sc-recon-row sc-recon-row--diff">
        <span className="sc-label">Reconciliation Difference</span>
        <strong
          className={
            scenario.reconciliationDifference !== 0 ? "bad-text" : "good-text"
          }
        >
          {scenario.reconciliationDifference === 0 ? (
            <span className="sc-recon sc-recon--ok">
              <Check size={11} /> Zero
            </span>
          ) : (
            formatVariance(scenario.reconciliationDifference)
          )}
        </strong>
      </div>
      <div className="sc-recon-row">
        <span className="sc-label">Reconciled</span>
        <ReconciliationStatus
          reconciled={scenario.reconciled}
          difference={scenario.reconciliationDifference}
        />
      </div>
      {!scenario.reconciled && (
        <div className="sc-recon-notice" data-testid="reconciliation-gap-notice">
          <AlertCircle size={13} />
          <p>
            The sum of driver impacts does not fully reconcile to the difference
            between baseline and outlook totals. Recompute or adjust drivers to
            close the gap before promoting.
          </p>
        </div>
      )}
    </div>
  );
}
